import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
} from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import format from 'date-fns-tz/format';
import { IdentificationType } from '@prisma/client';
import { getFullName } from '../../../helpers/global';
import { LoadedUser } from '../../../interfaces';
import { getIDUploadTitle } from '../../../helpers/waivers';

Font.register({
  family: 'Montserrat',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-.ttf',
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCu170w-.ttf',
      fontWeight: 'semibold',
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCuM70w-.ttf',
      fontWeight: 'bold',
    },
  ],
});

const PAGE_VERTICAL_PADDING = 32;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Montserrat',
    flexDirection: 'column',
    gap: 32,
    backgroundColor: '#FFFFFF',
    paddingVertical: PAGE_VERTICAL_PADDING,
  },
  section: {
    paddingHorizontal: 16,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionHeader: {
    textAlign: 'center',
    width: '100%',
    fontWeight: 'semibold',
    fontSize: 18,
  },
  text: {
    fontSize: 12,
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
  idSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
  },
  footerText: {
    fontSize: 8,
    textAlign: 'center',
    textDecoration: 'none',
    color: '#333',
  },
});

function Section({
  header,
  children,
  style,
  wrap = true,
}: React.PropsWithChildren<{
  header?: string;
  style?: Style;
  wrap?: boolean;
}>) {
  return (
    <View style={{ ...styles.section, ...style }} wrap={wrap}>
      {header && (
        <Text style={styles.sectionHeader} wrap={false}>
          {header}
        </Text>
      )}
      {children}
    </View>
  );
}

interface Props {
  user: LoadedUser;
  buffer: Buffer;
  idType: IdentificationType;
}

function ImagePDF({ user, buffer, idType }: Props) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed style={styles.footer}>
          <Link style={styles.footerText} href="https://www.getporter.io">
            Powered by Porter
          </Link>
        </View>

        <Section
          wrap={false}
          header={getIDUploadTitle(idType, 'Upload')}
          style={styles.idSection}>
          <Text style={styles.text} wrap={false}>
            Uploaded By:{' '}
            {user?.employee.displayName || (user ? getFullName(user) : '')}
          </Text>
          <Text style={styles.text} wrap={false}>
            Uploaded On: {format(new Date(), 'MM/dd/yyyy h:mm a zzz')}
          </Text>

          <Image
            src={buffer}
            style={{
              width: '50%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Section>
      </Page>
    </Document>
  );
}

export default ImagePDF;
